import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import dedicationsData from "../data/dedications.json";
import SEO from "./common/SEO";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMusic,
  faCalendarAlt,
  faQuoteLeft,
  faSearch,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";

const Dedications = () => {
  const { i18n } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedManuscript, setSelectedManuscript] = useState(null);

  const isEnglish = (i18n.language || "it").startsWith("en");
  const title = isEnglish ? "Musical Dedications" : "Dediche Musicali";
  const desc = isEnglish
    ? "Original musical works and compositions dedicated to the Nuova Orchestra da Camera Ferruccio Busoni."
    : "Opere musicali originali e composizioni dedicate alla Nuova Orchestra da Camera Ferruccio Busoni da eminenti compositori.";

  const filteredDedications = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return dedicationsData;
    return dedicationsData.filter(
      (d) =>
        d.composer.toLowerCase().includes(q) ||
        d.piece.toLowerCase().includes(q) ||
        d.dedication.toLowerCase().includes(q) ||
        d.date_location.toLowerCase().includes(q)
    );
  }, [searchQuery]);

  const manuscripts = [
    { src: "/img/ded/ded1.jpg", title: "Spartito autografo con dedica n. 1" },
    { src: "/img/ded/ded2.jpg", title: "Spartito autografo con dedica n. 2" },
    { src: "/img/ded/ded3.jpg", title: "Spartito autografo con dedica n. 3" },
    { src: "/img/ded/ded4.jpg", title: "Spartito autografo con dedica n. 4" },
  ];

  return (
    <>
      <SEO title={title} description={desc} />
      <section className="section subpage-section">
        <div className="container subpage-container">
          {/* Section Header */}
          <div className="text-center mb-4">
            <p className="text-muted max-w-700 mx-auto mb-0">
              {isEnglish
                ? "Original compositions dedicated to the Ferruccio Busoni Chamber Orchestra and Maestro Massimo Belli by prominent contemporary composers."
                : "Composizioni originali dedicate all'Orchestra da Camera Ferruccio Busoni e al Maestro Massimo Belli da importanti maestri e compositori del nostro tempo."}
            </p>
          </div>

          {/* Search bar */}
          <div className="interactive-filter-card p-4 rounded-4 shadow-sm mb-5 bg-white border">
            <div className="row g-3 align-items-center">
              <div className="col-md-8">
                <div className="modern-search-wrapper">
                  <FontAwesomeIcon icon={faSearch} className="modern-search-icon" />
                  <input
                    type="text"
                    className="form-control modern-search-input"
                    placeholder={
                      isEnglish
                        ? "Search by composer or piece (e.g. Allevi, Podda, Sofianopulo)..."
                        : "Cerca per compositore o opera (es. Allevi, Podda, Sofianopulo)..."
                    }
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    aria-label="Cerca brano o compositore"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="modern-search-clear"
                      onClick={() => setSearchQuery("")}
                      aria-label="Cancella ricerca"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  )}
                </div>
              </div>
              <div className="col-md-4 text-md-end text-muted small">
                {isEnglish
                  ? `Showing ${filteredDedications.length} dedications`
                  : `Visualizzazione di ${filteredDedications.length} dediche`}
              </div>
            </div>
          </div>

          {/* Dedication Cards Grid */}
          {filteredDedications.length > 0 ? (
            <div className="row g-4 mb-5">
              {filteredDedications.map((item, index) => (
                <div key={index} className="col-md-6">
                  <div className="card dedication-card h-100 border-0 shadow-sm rounded-4 p-4 bg-white position-relative">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <span className="composer-badge-pill">
                        {item.composer}
                      </span>
                      <FontAwesomeIcon icon={faMusic} className="text-warning opacity-75" />
                    </div>

                    <h4 className="dedication-piece-title mb-3">{item.piece}</h4>

                    <div className="dedication-quote-box p-3 rounded-3 mb-3 bg-light">
                      <FontAwesomeIcon icon={faQuoteLeft} className="text-warning me-2 small" />
                      <span className="fst-italic dedication-quote-text">
                        {item.dedication}
                      </span>
                    </div>

                    <div className="mt-auto pt-2 text-muted small d-flex align-items-center">
                      <FontAwesomeIcon icon={faCalendarAlt} className="text-secondary me-2" />
                      <span
                        dangerouslySetInnerHTML={{
                          __html: item.date_location.replace(/\n/g, " • "),
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-5 bg-white rounded-4 border shadow-sm mb-5">
              <p className="text-muted mb-0 fs-5">
                {isEnglish
                  ? "No dedications found matching your search."
                  : "Nessuna dedica trovata per i criteri cercati."}
              </p>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm mt-3 rounded-pill px-3"
                onClick={() => setSearchQuery("")}
              >
                {isEnglish ? "Reset search" : "Azzera ricerca"}
              </button>
            </div>
          )}

          {/* Historic Manuscripts Preview Gallery */}
          <div className="dedications-gallery-section pt-4 border-top">
            <div className="text-center mb-4">
              <h3 className="section-subheading mt-2">
                {isEnglish
                  ? "Historical Autographed Manuscripts"
                  : "Partiture e Spartiti Autografi"}
              </h3>
            </div>

            <div className="row g-4">
              {manuscripts.map((img, idx) => (
                <div key={idx} className="col-6 col-md-3">
                  <div
                    className="card manuscript-card border-0 shadow-sm rounded-4 overflow-hidden h-100 cursor-pointer"
                    onClick={() => setSelectedManuscript(img)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="manuscript-img-box">
                      <img
                        src={img.src}
                        alt={img.title}
                        className="img-fluid w-100 hoverzoom"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-2 text-center bg-white">
                      <small className="fw-semibold text-muted">{img.title}</small>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Modal Lightbox for Manuscript Score Preview */}
          {selectedManuscript && (
            <div
              className="modal-backdrop-custom d-flex align-items-center justify-content-center p-3"
              onClick={() => setSelectedManuscript(null)}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(9, 12, 30, 0.88)",
                zIndex: 9999,
              }}
            >
              <div
                className="modal-inner bg-white rounded-4 p-3 shadow-lg max-w-700 position-relative"
                onClick={(e) => e.stopPropagation()}
                style={{ maxWidth: "800px", width: "95%" }}
              >
                <button
                  type="button"
                  className="btn-close position-absolute top-0 end-0 m-3"
                  onClick={() => setSelectedManuscript(null)}
                  aria-label="Close"
                ></button>
                <img
                  src={selectedManuscript.src}
                  alt={selectedManuscript.title}
                  className="img-fluid rounded-3 w-100 mb-2"
                />
                <p className="text-center text-muted mb-0 small fw-semibold">
                  {selectedManuscript.title}
                </p>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default Dedications;
